import { Component, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { AdminService } from '../../../../core/services/admin.service';
import { LogHelper } from '../../../../core/helpers/log.helper';
import { AuthService } from '../../../../core/services/auth.service';
import { AlertService } from '../../../../shared/alert/alert.service';
import { TemplateService } from '../../../../core/services/template.service';
import { RoleNames } from 'app/core/constants/role-names';
import { AdminUser, UpdateAdminUserRequest } from "../../../../core/models/admin-user.model";
import { ManageAdminTrialsComponent } from "../manage-admin-trials/manage-admin-trials.component";
import { AdministratorTrial } from "../../../../core/models/administrator-trial.model";
import { environment } from 'environments/environment';

@Component({
  selector: 'app-edit-admin-user',
  templateUrl: './edit-admin-user.component.html',
  styleUrls: ['./edit-admin-user.component.scss']
})
export class EditAdminUserComponent implements OnInit {
  @ViewChild('manageTrials') manageTrials: ManageAdminTrialsComponent;

  dataHasLoaded = false;
  form: UntypedFormGroup;
  isFormProcessing = false;
  updatingAdminUserId: string;
  editRolesAvailable = true;

  selectedRoles: string[] = [];
  initialRoles: string[] = [];
  availableRoles: string[] = [
    RoleNames.ProductAdministrator,
    RoleNames.ProjectCoordinatorExpenses,
    RoleNames.ProjectCoordinatorTravel,
    RoleNames.BlindedProjectCoordinatorExpenses,
    RoleNames.StartupCoordinator,
    RoleNames.ProjectManager,
    RoleNames.CardManager,
    RoleNames.CallCentre,
    RoleNames.EndpointDataEditor
  ]
  rolesRetrieved: boolean = false;

  currentAdminId = '';
  currentUserId = '';
  selectedTab = 'info';
  hideFormButtons = false;
  administrator: AdminUser = new AdminUser();

  isMyProfile: boolean = false;
  changingPassword: boolean = false;

  constructor(private authService: AuthService,
    private adminService: AdminService, private alertService: AlertService,
    private router: Router, private activatedRoute: ActivatedRoute,
    private templateService: TemplateService) { }

  ngOnInit() {
    this.templateService.showHeader();

    this.form = new UntypedFormGroup({
      firstname: new UntypedFormControl('', Validators.required),
      lastname: new UntypedFormControl('', Validators.required),
      email: new UntypedFormControl({ disabled: true, value: '' }),
      muvUserId: new UntypedFormControl(''),
      assignToAllTrials: new UntypedFormControl(false),
      unassignFromAllTrials: new UntypedFormControl(false),
      canBypassEntra: new UntypedFormControl(false),
      newPassword: new UntypedFormControl(''),
      confirmNewPassword: new UntypedFormControl('')
    }, { validators: EditAdminUserComponent.passwordMatchValidator });

    this.activatedRoute.queryParams.subscribe(params => {
      const id = params.id;
      this.currentUserId = params.id;

      if (id == null) {
        this.alertService.showWarningAlert('User not found!');
        this.router.navigate(['/admin']);
      }

      this.adminService.retrieveAdminUser(id).subscribe({
        next: adminUser => {
          this.administrator = adminUser;
          this.manageTrials.setAdministrator(adminUser);

          this.selectedRoles = adminUser.roles;
          this.initialRoles = [...adminUser.roles];
          this.rolesRetrieved = true;
          this.updatingAdminUserId = adminUser.id;
          this.form.patchValue({
            firstname: adminUser.firstname,
            lastname: adminUser.lastname,
            email: adminUser.email,
            trialIds: adminUser.trials != null ? adminUser.trials.map(t => t.id) : [],
            muvUserId: adminUser.muvUserId,
            canBypassEntra: adminUser.canBypassEntra
          });
          this.form.get('canBypassEntra').disable();

          this.isMyProfile = adminUser.isCurrentAdmin && environment.environment !== "DEVELOPMENT" && environment.environment !== "STAGING";

          if (adminUser.isCurrentAdmin) {
            this.form.get("firstname").disable();
            this.form.get("lastname").disable();
          }

          this.dataHasLoaded = true;
        },
        error: error => {
          LogHelper.log(error);
          this.alertService.showWarningAlert(error);
        }
      });
    });

    this.addSystemAdminRoleToList();
  }

  updateAssignToAllTrialsValue(assignAllTrials: boolean) {
    this.form.patchValue({assignToAllTrials: assignAllTrials});
  }

  updateUnassignFromAllTrialsValue(unassignAllTrials: boolean) {
    this.form.patchValue({unassignFromAllTrials: unassignAllTrials});
  }

  addSystemAdminRoleToList() {
    if (this.authService.hasRole(RoleNames.SystemAdministrator) ||
      environment.environment === "DEVELOPMENT" ||
      environment.environment === "STAGING") {
      this.availableRoles.splice(0, 0, RoleNames.SystemAdministrator)
    }
  }

  onTabClick(selection: string) {
    this.selectedTab = selection;
  }

  static passwordMatchValidator(group: AbstractControl): ValidationErrors | null {
    const password = group.get('newPassword')?.value;
    const confirmPassword = group.get('confirmNewPassword')?.value;

    if (password && confirmPassword && password !== confirmPassword) {
      return { passwordMismatch: true };
    }
    return null;
  }

  toggleChangePassword() {
    this.changingPassword = true;
    const passwordControl = this.form.get('newPassword');
    const confirmPasswordControl = this.form.get('confirmNewPassword');
    passwordControl.setValidators(Validators.required);
    confirmPasswordControl.setValidators(Validators.required);
    passwordControl.updateValueAndValidity();
    confirmPasswordControl.updateValueAndValidity();
    this.form.markAsDirty();
  }

  cancelChangePassword() {
    this.changingPassword = false;
    const passwordControl = this.form.get('newPassword');
    const confirmPasswordControl = this.form.get('confirmNewPassword');
    passwordControl.clearValidators();
    confirmPasswordControl.clearValidators();
    passwordControl.setValue('');
    confirmPasswordControl.setValue('');
    passwordControl.updateValueAndValidity();
    confirmPasswordControl.updateValueAndValidity();
  }

  onFormSubmit() {
    if (!this.form.valid)
      return;

    const newPassword = this.form.get('newPassword').value;
    const confirmNewPassword = this.form.get('confirmNewPassword').value;

    if (newPassword && newPassword !== confirmNewPassword) {
      this.alertService.showWarningAlert('Passwords do not match.');
      return;
    }

    this.isFormProcessing = true;
    let request: UpdateAdminUserRequest = {
      id: this.updatingAdminUserId,
      firstname: this.form.get('firstname').value,
      lastname: this.form.get('lastname').value,
      roles: this.selectedRoles,
      adminTrials: this.administrator.trials,
      muvUserId: this.form.get('muvUserId').value,
      assignToAllTrials: this.form.get('assignToAllTrials').value,
      unassignFromAllTrials: this.form.get('unassignFromAllTrials').value,
      canBypassEntra: this.administrator.canBypassEntra,
      newPassword: newPassword || undefined
    }

    this.adminService.updateAdminUser(request).subscribe({
      next: () => {
        if (this.updatingAdminUserId === this.currentAdminId) {
          this.authService.getRolesFromApi().subscribe(() => {
            this.returnToAdministratorList();
          });
        } else {
          this.returnToAdministratorList();
        }

        this.isFormProcessing = false;
      },
      error: error => {
        this.isFormProcessing = false;
        LogHelper.log(error);
        this.alertService.showWarningAlert(error.error
          ? error.error.title
          : 'Sorry, there was a problem updating the account');
      }
    });
  }

  returnToAdministratorList() {
    this.alertService.showSuccessAlert('User successfully updated.');
    let queryParams = {
      page: this.adminService.currentPageNumber
    };
    this.router.navigate(['/admin'], { queryParams: queryParams });
  }

  selectRole(checkEvent: any, roleName: string) {
    if (checkEvent.target.checked) {
      this.selectedRoles.push(roleName);
    } else {
      this.selectedRoles = this.selectedRoles.filter(role => role !== roleName);
    }
    this.form.markAsDirty();
  }

  getRoleText(roleName: string): string {
    switch (roleName) {
      case RoleNames.SystemAdministrator:
        return "System Administrator";
      case RoleNames.ProductAdministrator:
        return "Product Administrator";
      case RoleNames.ProjectCoordinatorExpenses:
        return "Project Coordinator - Expenses";
      case RoleNames.ProjectCoordinatorTravel:
        return "Project Coordinator - Travel";
      case RoleNames.BlindedProjectCoordinatorExpenses:
        return "Blinded Coordinator - Expenses";
      case RoleNames.StartupCoordinator:
        return "Startup Coordinator";
      case RoleNames.ProjectManager:
        return "Project Manager";
      case RoleNames.CardManager:
        return "Card Manager";
      case RoleNames.CallCentre:
        return "Call Centre";
      case RoleNames.EndpointDataEditor:
        return "Endpoint Data Editor";
    }
  }

  userHasRole(role: string): boolean {
    return this.selectedRoles.indexOf(role) >= 0;
  }

  public findInvalidControls() {
    const invalid = [];
    const controls = this.form.controls;
    for (const name in controls) {
      if (controls[name].invalid) {
        invalid.push(name);
      }
    }
    return invalid;
  }

  onAssignTrials(adminTrials: AdministratorTrial[]): void {
    this.administrator.trials = adminTrials;
    this.form.markAsDirty();
  }

  updateFormButtonVisibility(hide: boolean): void {
    this.hideFormButtons = hide;
  }
}
